import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';

import {
  useSoccerMatches,
  usePrepareSoccerMatches,
  useTurnOnSoccerMatches,
  useTurnOffSoccerMatches,
  type SoccerMatchRow,
  type SoccerMatchBucketRow,
  type SoccerOpenerBucket,
  type PrepareSoccerMatchesResult,
  type SoccerMatchSemanticsSummary
} from '../../api/useSoccerMatches';
import {
  MARKET_SHAPE_LABELS,
  SEMANTICS_REASON_LABELS,
  SOCCER_MATCH_PATH_LABELS,
  SOCCER_OPENER_BUCKET_LABELS,
  aggregatePrepareResults,
  areAllMatchesSelected,
  bucketsAcrossMatches,
  buildMatchPrepareRequests,
  computeSelectionCounts,
  deriveBucketStatus,
  deriveMatchStatus,
  findAutoExpandMatchKey,
  formatArmDirectiveCaption,
  formatBucketConfigCaption,
  formatBucketStatusLabel,
  formatSemanticsTally,
  hasArmedSelection,
  isBucketColumnFullySelected,
  isBucketSelected,
  isMatchFullySelected,
  isMatchPartiallySelected,
  resolveTurnOnRuleIds,
  setAllMatchesSelection,
  setMatchSelection,
  toggleBucketCell,
  toggleBucketColumn,
  type BucketStatus,
  type ExistingRuleMakerState
} from './soccerMatchSelection';
import { formatEventStart } from './entrySort';

interface SoccerMatchDrawerProps {
  open: boolean;
  onClose: () => void;
  /** null only while the caller's own group resolution hasn't settled yet —
   *  the drawer is not expected to be openable in that state. */
  groupId: string | null;
  groupLabel: string | null;
  /** Active entry Path — governs which two maker flags Turn On/Off flip. */
  tab: 'a' | 'b';
  /** The page's existing pairId -> rule map (`anyRulesByPairId`), used to
   *  resolve rule ids AND current maker flags for pairs the prepare response
   *  doesn't cover (already had a rule, so the backend `skipped` it rather
   *  than re-creating it) — see `resolveTurnOnRuleIds`'s capital-risk guard. */
  rulesByPairId: ReadonlyMap<string, ExistingRuleMakerState>;
}

const STATUS_COLORS: Record<BucketStatus['kind'], string> = {
  OFF: 'rgba(255,255,255,0.15)',
  PARTIAL: 'rgba(255,193,7,0.35)',
  READY: 'rgba(33,150,243,0.35)',
  ON: 'rgba(34,197,94,0.35)'
};

const StatusChip = ({ status }: { status: BucketStatus }) => (
  <Chip
    size="small"
    label={formatBucketStatusLabel(status)}
    sx={{
      bgcolor: STATUS_COLORS[status.kind],
      color: 'white',
      fontSize: '0.65rem',
      height: 20
    }}
  />
);

/**
 * Soccer Match Drawer — turn a whole mapped soccer match (or a slice of its
 * markets, bucket by bucket) on or off in one operator gesture, instead of
 * running the uncommitted `club-soccer-rules-tiered.js` script or flipping
 * ~44 rows by hand. Mirrors `EnginePositionDrawer`'s shell exactly (right
 * anchor, `#1a1a2e` panel, header Stack + close icon).
 *
 * Status chips are bound ONLY to the read model's `state` (server truth,
 * refetched after every settle) — never to the `selected` checkboxes or an
 * optimistic guess, matching the armed-switch invariant the rest of this
 * page relies on (`armSwitchState.ts`).
 */
export const SoccerMatchDrawer = ({
  open,
  onClose,
  groupId,
  groupLabel,
  tab,
  rulesByPairId
}: SoccerMatchDrawerProps) => {
  const { data, isLoading, refetch, isFetching } = useSoccerMatches(groupId, open);
  const matches = useMemo(() => data?.matches ?? [], [data]);
  const unclassifiedPairCount = data?.unclassifiedPairCount ?? 0;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedMatchKey, setExpandedMatchKey] = useState<string | null>(null);
  const autoExpandedRef = useRef(false);

  // Fresh selection + auto-expand every time the drawer (re)opens or the
  // group underneath it changes — a stale selection from a previous group
  // must never carry over.
  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    autoExpandedRef.current = false;
  }, [open, groupId]);

  useEffect(() => {
    if (!open || autoExpandedRef.current || matches.length === 0) return;
    autoExpandedRef.current = true;
    setExpandedMatchKey(findAutoExpandMatchKey(matches));
  }, [open, matches]);

  const prepareMutation = usePrepareSoccerMatches();
  const turnOnMutation = useTurnOnSoccerMatches();
  const turnOffMutation = useTurnOffSoccerMatches();

  const [confirmAction, setConfirmAction] = useState<'on' | 'off' | null>(null);
  const [preview, setPreview] = useState<PrepareSoccerMatchesResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const counts = useMemo(
    () => computeSelectionCounts(selected, matches),
    [selected, matches]
  );
  const allSelected = useMemo(
    () => areAllMatchesSelected(selected, matches),
    [selected, matches]
  );
  const bucketColumns = useMemo(() => bucketsAcrossMatches(matches), [matches]);
  const canTurnOff = useMemo(
    () => hasArmedSelection(selected, matches),
    [selected, matches]
  );

  // Turn On's "skipped" pairs (already have an active rule) are NOT all
  // "left untouched" as the dialog used to claim unqualified: a skipped pair
  // whose existing maker flags resolve to a known direction (path/no-only)
  // gets its flags REWRITTEN to match the currently active tab —
  // `resolveTurnOnRuleIds`'s own fallback classification decides this, so
  // reuse it here to give the operator an honest breakdown BEFORE they
  // confirm, instead of after (via the post-action toast) when it's too late
  // to reconsider. Only the genuinely-unresolved subset is actually untouched.
  const skippedBreakdown = useMemo(() => {
    if (!preview || preview.skipped.length === 0) return null;
    const dedupedSkippedCount = new Set(preview.skipped.map(s => s.pairId)).size;
    const { unresolvedPairIds } = resolveTurnOnRuleIds(preview, rulesByPairId);
    // unresolvedPairIds conflates two different reasons — a pair with an
    // existing rule in an unrecognized flag state (rulesByPairId HAS an
    // entry) vs. a pair with no rule at all, e.g. skipped for `open-order`
    // (rulesByPairId has no entry). Splitting them avoids telling an
    // operator "check the flags" on a pair that has no rule to have flags.
    const ambiguousFlags = unresolvedPairIds.filter(id => rulesByPairId.has(id)).length;
    return {
      reArmed: dedupedSkippedCount - unresolvedPairIds.length,
      ambiguousFlags,
      noRuleFound: unresolvedPairIds.length - ambiguousFlags
    };
  }, [preview, rulesByPairId]);

  // Both actions resolve their honest counts via a `dryRun:true` preview —
  // Turn Off's REAL execution also rides `dryRun:true` (it never writes;
  // see `useTurnOffSoccerMatches`), so this preview and Turn Off's own rule-
  // id resolution ask the backend the exact same, side-effect-free question.
  const openConfirm = useCallback(
    async (action: 'on' | 'off') => {
      if (!groupId) return;
      setConfirmAction(action);
      setPreview(null);
      setPreviewLoading(true);
      try {
        const requests = buildMatchPrepareRequests(selected, matches);
        const results: PrepareSoccerMatchesResult[] = [];
        for (const req of requests) {
          const result = await prepareMutation.mutateAsync({
            groupId,
            matchKeys: [req.matchKey],
            buckets: req.buckets,
            path: tab,
            dryRun: true
          });
          results.push(result);
        }
        setPreview(aggregatePrepareResults(results));
      } finally {
        setPreviewLoading(false);
      }
    },
    [groupId, selected, matches, tab, prepareMutation]
  );

  const runAction = useCallback(
    async (action: 'on' | 'off') => {
      if (!groupId) return;
      const requests = buildMatchPrepareRequests(selected, matches);
      if (requests.length === 0) return;
      const result =
        action === 'on'
          ? await turnOnMutation.mutateAsync({ groupId, path: tab, requests, rulesByPairId })
          : await turnOffMutation.mutateAsync({ groupId, path: tab, requests, rulesByPairId });
      if (result.unresolvedPairIds.length > 0) {
        const n = result.unresolvedPairIds.length;
        const pair = n === 1 ? 'pair' : 'pairs';
        if (action === 'on') {
          // unresolvedPairIds conflates two different reasons (see
          // `skippedBreakdown` above, computed pre-confirm from the same
          // classification) — split them here too so the toast never tells
          // an operator to "check the flags" on a pair that never had a
          // rule to have flags. Both are genuinely actionable: the pair was
          // NOT touched despite the confirm dialog's "ARMS every resulting
          // rule" claim.
          const ambiguousFlags = result.unresolvedPairIds.filter(id =>
            rulesByPairId.has(id)
          );
          const noRuleFound = result.unresolvedPairIds.filter(
            id => !rulesByPairId.has(id)
          );
          if (ambiguousFlags.length > 0) {
            const m = ambiguousFlags.length;
            toast(
              `${m} ${m === 1 ? 'pair' : 'pairs'} left untouched — existing rule state could not be resolved to a direction. Check manually.`,
              { theme: 'dark', type: 'warning', autoClose: false }
            );
          }
          if (noRuleFound.length > 0) {
            const k = noRuleFound.length;
            toast(
              `${k} ${k === 1 ? 'pair' : 'pairs'} left untouched — no matching rule was found.`,
              { theme: 'dark', type: 'warning', autoClose: false }
            );
          }
        } else {
          // Turn Off's unresolvedPairIds means "no rule was ever found for
          // this pair" (see useTurnOffSoccerMatches) — routinely true for a
          // whole-match selection that swept in a never-armed bucket, NOT a
          // sign anything is wrong. Informational, not a standing warning.
          toast(`${n} selected ${pair} had no rule to disarm — nothing to do.`, {
            theme: 'dark',
            type: 'info',
            autoClose: 4000
          });
        }
      }
    },
    [groupId, selected, matches, tab, rulesByPairId, turnOnMutation, turnOffMutation]
  );

  const running = turnOnMutation.isLoading || turnOffMutation.isLoading;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted={false}
      PaperProps={{
        sx: {
          width: { xs: '100vw', md: '46vw' },
          maxWidth: '680px',
          bgcolor: '#1a1a2e',
          color: 'white'
        }
      }}
    >
      <Box
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}
        >
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Matches
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              {groupLabel ?? '—'} · {matches.length} match
              {matches.length === 1 ? '' : 'es'} · {SOCCER_MATCH_PATH_LABELS[tab]}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {isFetching && <CircularProgress size={14} sx={{ color: 'white' }} />}
            <IconButton
              onClick={() => void refetch()}
              sx={{ color: 'white' }}
              size="small"
              aria-label="Refresh matches"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={!allSelected && counts.bucketCellCount > 0}
              onChange={e => setSelected(setAllMatchesSelection(matches, e.target.checked))}
              sx={{ color: 'white' }}
              inputProps={{ 'aria-label': 'Select all matches' }}
            />
            <Typography variant="body2">Select all matches</Typography>
          </Stack>

          {bucketColumns.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {bucketColumns.map(bucket => {
                const checked = isBucketColumnFullySelected(selected, matches, bucket);
                return (
                  <Chip
                    key={bucket}
                    size="small"
                    clickable
                    variant={checked ? 'filled' : 'outlined'}
                    color={checked ? 'primary' : 'default'}
                    label={SOCCER_OPENER_BUCKET_LABELS[bucket]}
                    onClick={() =>
                      setSelected(toggleBucketColumn(selected, matches, bucket, !checked))
                    }
                    sx={{ color: checked ? undefined : 'white', borderColor: 'rgba(255,255,255,0.3)' }}
                  />
                );
              })}
            </Stack>
          )}

          {unclassifiedPairCount > 0 && (
            <Box
              sx={{
                border: '1px solid rgba(255,193,7,0.4)',
                borderRadius: 1,
                p: 1,
                mb: 2,
                bgcolor: 'rgba(255,193,7,0.08)'
              }}
            >
              <Typography variant="caption" sx={{ color: '#ffc107' }}>
                {unclassifiedPairCount} pair{unclassifiedPairCount === 1 ? '' : 's'} could not be
                classified — they will NOT be prepared.
              </Typography>
            </Box>
          )}

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : matches.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              No soccer matches in this group.
            </Typography>
          ) : (
            matches.map(match => (
              <MatchSection
                key={match.matchKey}
                match={match}
                expanded={expandedMatchKey === match.matchKey}
                onToggleExpand={() =>
                  setExpandedMatchKey(cur => (cur === match.matchKey ? null : match.matchKey))
                }
                selected={selected}
                onToggleMatch={checked => setSelected(setMatchSelection(selected, match, checked))}
                onToggleBucket={bucket =>
                  setSelected(toggleBucketCell(selected, match.matchKey, bucket))
                }
              />
            ))
          )}
        </Box>

        {/* Footer */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', flex: 1 }}>
            {counts.bucketCellCount > 0
              ? `${counts.matchCount} match${counts.matchCount === 1 ? '' : 'es'} · ${counts.pairCount} pairs selected`
              : 'Nothing selected'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!groupId || counts.bucketCellCount === 0 || running}
            onClick={() => void openConfirm('on')}
          >
            {turnOnMutation.isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              'Turn on match'
            )}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={!groupId || counts.bucketCellCount === 0 || !canTurnOff || running}
            onClick={() => void openConfirm('off')}
          >
            {turnOffMutation.isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              'Turn off'
            )}
          </Button>
        </Stack>
      </Box>

      <Dialog open={confirmAction !== null} onClose={() => setConfirmAction(null)}>
        <DialogTitle sx={{ fontSize: '0.95rem' }}>
          {confirmAction === 'on' ? 'Turn on' : 'Turn off'} {counts.matchCount} match
          {counts.matchCount === 1 ? '' : 'es'}?
        </DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <DialogContentText sx={{ fontSize: '0.85rem' }}>
              {SOCCER_MATCH_PATH_LABELS[tab]}.{' '}
              {confirmAction === 'on' ? (
                <>
                  This assigns {preview?.pairs.assigned ?? 0} pair(s) to their bucket subgroup,
                  enables {preview?.pairs.enabled ?? 0}, creates {preview?.rules.created ?? 0} new
                  rule(s) ({preview?.rules.skipped ?? 0} already existed), and ARMS every
                  resulting rule.
                  {skippedBreakdown && skippedBreakdown.reArmed > 0 && (
                    <>
                      {' '}
                      {skippedBreakdown.reArmed} pair(s) already have an active rule and will be
                      RE-ARMED under {SOCCER_MATCH_PATH_LABELS[tab]} (their maker flags will be
                      switched to match, even if currently armed under the other Path).
                    </>
                  )}
                  {skippedBreakdown && skippedBreakdown.ambiguousFlags > 0 && (
                    <>
                      {' '}
                      {skippedBreakdown.ambiguousFlags} pair(s) already have an active rule in an
                      unrecognized flag state and will be left untouched — check them manually
                      after this action.
                    </>
                  )}
                  {skippedBreakdown && skippedBreakdown.noRuleFound > 0 && (
                    <>
                      {' '}
                      {skippedBreakdown.noRuleFound} pair(s) were skipped with no matching rule
                      found (e.g. an open order) and will be left untouched.
                    </>
                  )}
                  {preview && preview.subgroups.updated > 0 && (
                    <>
                      {' '}
                      {preview.subgroups.updated} config subgroup(s) already exist for these
                      buckets — their config will be RESET to the standing default, overwriting
                      any manual tuning.
                    </>
                  )}
                </>
              ) : (
                <>
                  This disarms the rule behind {preview?.pairs.matched ?? counts.pairCount}{' '}
                  selected pair(s). Market data and the rules themselves are left alone — this is
                  reversible.
                </>
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            color={confirmAction === 'off' ? 'error' : 'primary'}
            disabled={previewLoading}
            onClick={() => {
              const action = confirmAction;
              setConfirmAction(null);
              setPreview(null);
              if (action) void runAction(action);
            }}
            autoFocus
          >
            {confirmAction === 'on' ? 'Turn on' : 'Turn off'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

const MatchSection = ({
  match,
  expanded,
  onToggleExpand,
  selected,
  onToggleMatch,
  onToggleBucket
}: {
  match: SoccerMatchRow;
  expanded: boolean;
  onToggleExpand: () => void;
  selected: ReadonlySet<string>;
  onToggleMatch: (checked: boolean) => void;
  onToggleBucket: (bucket: SoccerOpenerBucket) => void;
}) => {
  const status = deriveMatchStatus(match);
  const kickoffMs = match.kickoff ? new Date(match.kickoff).getTime() : null;
  const kickoffLabel =
    kickoffMs != null && !Number.isNaN(kickoffMs)
      ? formatEventStart(kickoffMs, Date.now())
      : '—';
  const fullySelected = isMatchFullySelected(selected, match);
  const partiallySelected = isMatchPartiallySelected(selected, match);
  const tickerLabel = match.gameSuffix ? `${match.family}/${match.gameSuffix}` : match.family;
  // The ticker stays as the fallback title (a match whose pairs carry no
  // event title on either platform must still be identifiable), and as the
  // secondary caption otherwise — it's what ties a row back to the Entry
  // table and to Kalshi.
  const matchLabel = match.matchName ?? tickerLabel;

  return (
    <Box sx={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1, mb: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ p: 1, cursor: 'pointer' }}
        onClick={onToggleExpand}
      >
        <Checkbox
          size="small"
          checked={fullySelected}
          indeterminate={partiallySelected}
          onClick={e => e.stopPropagation()}
          onChange={e => onToggleMatch(e.target.checked)}
          sx={{ color: 'white' }}
          // Keyed on the ticker, never the display name: family/gameSuffix is
          // unique per match by construction, a name is not, and two rows with
          // the same accessible name are indistinguishable to a screen reader.
          inputProps={{ 'aria-label': `Select match ${tickerLabel}` }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {matchLabel}
            </Typography>
            <EventLink label="K" title="Open on Kalshi" url={match.kalshiEventUrl} />
            <EventLink label="P" title="Open on Polymarket" url={match.polyEventUrl} />
          </Stack>
          {/* `component="div"`: MUI maps `caption` to a <span>, and CSS
              overflow/text-overflow do not apply to a non-replaced inline
              box — `noWrap` would then suppress the wrap without ever
              ellipsizing, overflowing the row and giving the whole match
              list a horizontal scrollbar. */}
          <Typography
            component="div"
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.5)' }}
            noWrap
          >
            {kickoffLabel} · {match.pairCount} pairs
            {match.matchName ? ` · ${tickerLabel}` : ''}
            {match.semantics ? ` · ${describedCaption(match.semantics)}` : ''}
          </Typography>
        </Box>
        <StatusChip status={status} />
        <IconButton
          size="small"
          sx={{ color: 'white' }}
          onClick={e => {
            // The row itself also toggles on click; stop the bubble so this
            // button doesn't fire the toggle twice (which would cancel out).
            e.stopPropagation();
            onToggleExpand();
          }}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        {match.semantics && <SemanticsSummary semantics={match.semantics} />}
        <Box sx={{ p: 1 }}>
          {match.buckets.map(bucket => (
            <BucketRow
              key={bucket.bucket}
              matchKey={match.matchKey}
              bucket={bucket}
              checked={isBucketSelected(selected, match.matchKey, bucket.bucket)}
              onToggle={() => onToggleBucket(bucket.bucket)}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * One-letter deep link to the match's event page on a platform. Renders
 * nothing when the backend resolved no URL — never a dead link, and never a
 * fabricated one built from a ticker (the URL shape differs per Kalshi
 * series and per Polymarket event page).
 */
const EventLink = ({
  label,
  title,
  url
}: {
  label: string;
  title: string;
  url: string | null;
}) => {
  if (!url) return null;
  return (
    <Chip
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      clickable
      size="small"
      label={label}
      title={title}
      aria-label={title}
      // The header row toggles expand on click — a link must not also
      // collapse the match it just opened.
      onClick={e => e.stopPropagation()}
      sx={{
        height: 18,
        fontSize: '0.62rem',
        fontWeight: 600,
        color: 'white',
        borderColor: 'rgba(255,255,255,0.3)',
        // Chip defaults to flex-shrink:1, so a long match name would shrink
        // these alongside the title instead of ellipsizing the title alone —
        // squeezing the K/P glyphs down to unreadable stubs.
        flexShrink: 0,
        '& .MuiChip-label': { px: 0.75 }
      }}
      variant="outlined"
    />
  );
};

/** `"38 described"`, or `"38 described · 2 unresolved"` when any failed. */
function describedCaption(semantics: SoccerMatchSemanticsSummary): string {
  const parts = [`${semantics.resolved} described`];
  if (semantics.unresolved > 0) parts.push(`${semantics.unresolved} unresolved`);
  return parts.join(' · ');
}

/**
 * What the market-semantics deriver made of this match — the operator's only
 * window onto it until the detector that consumes it exists.
 *
 * Deliberately match-level: the drawer has no per-pair row, so the SHAPE
 * DISTRIBUTION is what makes a mis-derivation visible. A single flipped
 * `yesMeaning` is invisible in aggregate; "0 Moneyline in a 46-pair match" or
 * "40 unresolved" is not.
 *
 * `out of scope` is shown apart from `unresolved` because corners and player
 * props are markets the deriver RECOGNISES and does not describe — counting
 * them as failures would train the operator to ignore the number.
 */
function SemanticsSummary({
  semantics
}: {
  semantics: SoccerMatchSemanticsSummary;
}) {
  const { roster, resolved, outOfScope, unresolved, reasons, byShape } = semantics;
  return (
    <Box sx={{ px: 1.5, py: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', width: 74 }}>
          Semantics
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
          {roster
            ? roster.map(c => `${c.code} (${c.label})`).join(' · ')
            : 'no roster — the match names no two competitors'}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', width: 74 }}>
          Described
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
          {resolved}
          {outOfScope > 0 ? ` · ${outOfScope} out of scope` : ''}
          {unresolved > 0 ? ` · ${unresolved} unresolved` : ''}
        </Typography>
      </Stack>
      {Object.keys(byShape).length > 0 && (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', width: 74 }}>
            By shape
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {formatSemanticsTally(byShape, MARKET_SHAPE_LABELS)}
          </Typography>
        </Stack>
      )}
      {Object.keys(reasons).length > 0 && (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', width: 74 }}>
            Skipped
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {formatSemanticsTally(reasons, SEMANTICS_REASON_LABELS)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

const BucketRow = ({
  matchKey,
  bucket,
  checked,
  onToggle
}: {
  matchKey: string;
  bucket: SoccerMatchBucketRow;
  checked: boolean;
  onToggle: () => void;
}) => {
  const status = deriveBucketStatus(bucket.state, bucket.pairCount);
  const armDirectiveCaption = formatArmDirectiveCaption(
    bucket.armDirectiveCounts,
    bucket.pairCount
  );
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
      <Checkbox
        size="small"
        checked={checked}
        onChange={onToggle}
        sx={{ color: 'white' }}
        inputProps={{ 'aria-label': `${bucket.bucket} in ${matchKey}` }}
      />
      <Typography variant="body2" sx={{ width: 130, flexShrink: 0 }}>
        {SOCCER_OPENER_BUCKET_LABELS[bucket.bucket]}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: 'rgba(255,255,255,0.5)', width: 70, flexShrink: 0 }}
      >
        ({bucket.pairCount} pairs)
      </Typography>
      <StatusChip status={status} />
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', flex: 1 }}>
        {formatBucketConfigCaption(bucket.config)}
      </Typography>
      {armDirectiveCaption && (
        <Chip
          size="small"
          label={armDirectiveCaption}
          sx={{
            bgcolor: 'rgba(244,67,54,0.25)',
            color: 'white',
            fontSize: '0.62rem',
            height: 18
          }}
        />
      )}
    </Stack>
  );
};
